import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

type SetFiltersAction<TFilters extends Record<string, any>> =
  | Partial<TFilters>
  | ((prev: TFilters) => Partial<TFilters> | TFilters);

type QueryFilterNavigationOptions = {
  replace?: boolean;
};

const cloneFilterValue = <T,>(value: T): T => {
  if (Array.isArray(value)) {
    return [...value] as T;
  }

  return value;
};

const cloneFilters = <TFilters extends Record<string, any>>(filters: TFilters): TFilters => {
  const result = {} as TFilters;

  (Object.keys(filters) as Array<keyof TFilters>).forEach((key) => {
    result[key] = cloneFilterValue(filters[key]);
  });

  return result;
};

const isArrayEqual = (left: unknown[], right: unknown[]): boolean => {
  if (left.length !== right.length) return false;
  return left.every((item, index) => item === right[index]);
};

const isEqualToDefault = (value: unknown, defaultValue: unknown): boolean => {
  if (Array.isArray(value) && Array.isArray(defaultValue)) {
    return isArrayEqual(value, defaultValue);
  }

  return value === defaultValue;
};

const castScalar = (rawValue: string, defaultValue: unknown): unknown => {
  if (typeof defaultValue === 'number') {
    const parsedNumber = Number(rawValue);
    return Number.isFinite(parsedNumber) ? parsedNumber : defaultValue;
  }

  if (typeof defaultValue === 'boolean') {
    if (rawValue === 'true' || rawValue === '1') return true;
    if (rawValue === 'false' || rawValue === '0') return false;
    return defaultValue;
  }

  return rawValue;
};

const castFromQuery = (rawValues: string[], defaultValue: unknown): unknown => {
  if (Array.isArray(defaultValue)) {
    const sampleValue = defaultValue.length > 0 ? defaultValue[0] : '';
    return rawValues.map((value) => castScalar(value, sampleValue));
  }

  if (rawValues.length === 0) {
    return defaultValue;
  }

  return castScalar(rawValues[0], defaultValue);
};

const parseFiltersFromQuery = <TFilters extends Record<string, any>>(
  defaults: TFilters,
  searchParams: URLSearchParams
): TFilters => {
  const initialFilters = cloneFilters(defaults);

  (Object.keys(defaults) as Array<keyof TFilters>).forEach((key) => {
    const defaultValue = defaults[key];
    const keyName = String(key);
    const rawValues = Array.isArray(defaultValue)
      ? searchParams.getAll(keyName)
      : [searchParams.get(keyName)].filter((value): value is string => value !== null);

    if (rawValues.length === 0) {
      return;
    }

    initialFilters[key] = castFromQuery(rawValues, defaultValue) as TFilters[keyof TFilters];
  });

  return initialFilters;
};

export const useQueryFilters = <TFilters extends Record<string, any>>(defaultFilters: TFilters) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaults = useMemo(() => cloneFilters(defaultFilters), [defaultFilters]);

  const searchString = searchParams.toString();

  const filters = useMemo(
    () => parseFiltersFromQuery(defaults, new URLSearchParams(searchString)),
    [defaults, searchString]
  );

  const setSingleFilter = useCallback(
    <K extends keyof TFilters>(params: URLSearchParams, key: K, value: TFilters[K]) => {
      const keyName = String(key);
      const defaultValue = defaults[key];
      params.delete(keyName);

      if (isEqualToDefault(value, defaultValue)) {
        return;
      }

      if (Array.isArray(value)) {
        (value as unknown[]).forEach((item: unknown) => {
          params.append(keyName, String(item));
        });
        return;
      }

      params.set(keyName, String(value));
    },
    [defaults]
  );

  const setFilter = useCallback(
    <K extends keyof TFilters>(
      key: K,
      value: TFilters[K],
      options?: QueryFilterNavigationOptions
    ) => {
      setSearchParams((prevParams) => {
        const nextParams = new URLSearchParams(prevParams);
        setSingleFilter(nextParams, key, value);
        if (nextParams.toString() === prevParams.toString()) {
          return prevParams;
        }
        return nextParams;
      }, { replace: options?.replace ?? false });
    },
    [setSearchParams, setSingleFilter]
  );

  const setFilters = useCallback((
    action: SetFiltersAction<TFilters>,
    options?: QueryFilterNavigationOptions
  ) => {
    setSearchParams((prevParams) => {
      const nextParams = new URLSearchParams(prevParams);
      const prevFilters = parseFiltersFromQuery(defaults, prevParams);
      const partial = typeof action === 'function' ? action(prevFilters) : action;

      (Object.keys(partial) as Array<keyof TFilters>).forEach((key) => {
        const value = partial[key];
        if (value !== undefined) {
          setSingleFilter(nextParams, key, value as TFilters[keyof TFilters]);
        }
      });

      if (nextParams.toString() === prevParams.toString()) {
        return prevParams;
      }

      return nextParams;
    }, { replace: options?.replace ?? false });
  }, [defaults, setSearchParams, setSingleFilter]);

  const resetFilters = useCallback(() => {
    setSearchParams((prevParams) => {
      const nextParams = new URLSearchParams(prevParams);

      (Object.keys(defaults) as Array<keyof TFilters>).forEach((key) => {
        nextParams.delete(String(key));
      });

      return nextParams;
    }, { replace: true });
  }, [defaults, setSearchParams]);

  return { filters, setFilter, setFilters, resetFilters };
};
